import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

import { DARK_COLOR, ICON_COLOR_VARIANT, ICON_TEXT_ALIGN, IconConfiguration, LIGHT_COLOR } from '../icon-configuration';
import { IconContent } from './icon-content';

@Component({
  selector: 'celum-icon',
  templateUrl: './icon.html',
  styleUrls: ['./icon.less'],
  host: {
    class: 'celum-icon',
    '[class.celum-icon--display]': '!clickable()'
  },
  imports: [TranslatePipe, MatIconModule, NgStyle, IconContent, MatTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class CelumIcon implements OnChanges {
  public configuration = input(new IconConfiguration(undefined));
  public clickable = input(true);

  public iconStyle: any = {};

  private darkColor: string = DARK_COLOR;
  private lightColor: string = LIGHT_COLOR;
  private hoverActive = false;

  private changeDetectorRef = inject(ChangeDetectorRef);
  private el = inject(ElementRef);

  public ngOnChanges({ configuration }: SimpleChanges): void {
    if (this.configuration()?.icon) {
      this.iconStyle = this.getIconStyle();
      this.changeDetectorRef.markForCheck();
    }

    // TODO solve with "HostBinding('class') after update to ivy!
    if (configuration && configuration.currentValue) {
      const oldCls = configuration.previousValue ? configuration.previousValue.cssCls : '';
      const newCls = configuration.currentValue.cssCls;

      if (oldCls !== newCls) {
        oldCls && this.el.nativeElement.classList.remove(oldCls);
        newCls && this.el.nativeElement.classList.add(newCls);
      }
    }
  }

  @HostListener('mouseenter')
  public onMouseEnter(): void {
    this.toggleHover(true);
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    this.toggleHover(false);
  }

  public toggleHover(hover: boolean): void {
    this.hoverActive = hover;

    // eslint-disable-next-line eqeqeq
    if (this.configuration().hoverColor != null || this.configuration().textHoverColor) {
      this.iconStyle = this.getIconStyle();
      this.changeDetectorRef.markForCheck();
    }
  }

  public getTextAlignment(): any {
    let direction: string;

    switch (this.configuration().textAlign) {
      case ICON_TEXT_ALIGN.left:
        direction = 'row-reverse';
        break;
      case ICON_TEXT_ALIGN.right:
        direction = 'row';
        break;
      case ICON_TEXT_ALIGN.top:
        direction = 'column-reverse';
        break;
      case ICON_TEXT_ALIGN.bottom:
        direction = 'column';
        break;
      default:
        direction = 'row';
        break;
    }
    return {
      'flex-direction': direction
    };
  }

  public getTextColor(): string {
    const baseColor = this.configuration().textColor || (this.configuration().colorVariant === ICON_COLOR_VARIANT.dark ? this.darkColor : this.lightColor);

    if (this.hoverActive) {
      return this.configuration().textHoverColor || baseColor;
    } else {
      return baseColor;
    }
  }

  public getIconStyle(): any {
    const sizeWidth = this.configuration().iconWidth / 10 + 'rem';
    const sizeHeight = this.configuration().iconHeight / 10 + 'rem';

    let color = this.configuration().color || (this.configuration().colorVariant === ICON_COLOR_VARIANT.dark ? this.darkColor : this.lightColor);
    if (this.hoverActive) {
      color = this.configuration().hoverColor || color;
    }

    const boxShadow = this.configuration().outlineColor ? `0 0 0 ${this.configuration().outlineSize}rem ${this.configuration().outlineColor}` : null;
    return {
      width: sizeWidth,
      height: sizeHeight,
      lineHeight: '0',
      color,
      background: this.configuration().backgroundColor,
      boxShadow,
      ...this.configuration().customStyles
    };
  }
}
